import {
  Box,
  FormControl,
  FormErrorMessage,
  FormLabel,
  Select,
} from '@chakra-ui/react';
import type { SelectOption } from '@diamond/shared/types';
import {
  disabledInputStyles,
  MultiSelect,
  reactSelectStyles,
} from '@diamond/shared/ui';
import { useEffect } from 'react';
import type { UseFormReturn } from 'react-hook-form';

import {
  ACCOUNT_AGE_SELECT_OPTIONS,
  ACQUISITION_TYPE_SELECT_OPTIONS,
} from '../../../constants';
import type { CreateVoucherRequest } from '../../../types';

interface GeneralTargetSectionProps {
  register: UseFormReturn<CreateVoucherRequest>['register'];
  errors: UseFormReturn<CreateVoucherRequest>['formState']['errors'];
  customerTypeOptions: SelectOption[];
  customerTypeValue: SelectOption[];
  branchOptions: SelectOption[];
  branchValue: SelectOption[];
  restaurantCategoryOptions: SelectOption[];
  restaurantCategoryValue: SelectOption[];
  onCustomerTypesChange: (options: ReadonlyArray<SelectOption> | null) => void;
  onBranchesChange: (options: ReadonlyArray<SelectOption> | null) => void;
  onRestaurantCategoriesChange: (
    options: ReadonlyArray<SelectOption> | null
  ) => void;
  isLoadingOptions?: boolean;
  disabled?: boolean;
  form: UseFormReturn<CreateVoucherRequest>;
}

export const GeneralTargetSection = ({
  register,
  errors,
  customerTypeOptions,
  customerTypeValue,
  branchOptions,
  branchValue,
  restaurantCategoryOptions,
  restaurantCategoryValue,
  onCustomerTypesChange,
  onBranchesChange,
  onRestaurantCategoriesChange,
  isLoadingOptions = false,
  disabled = false,
  form,
}: GeneralTargetSectionProps) => {
  const acquisitionType = form.watch(
    'acquisition_configuration.acquisition_type'
  );
  const showAccountAgeField = acquisitionType === 'ACCOUNT_ACTIVATION';

  // Clear the value when switching to REGULAR
  useEffect(() => {
    if (acquisitionType === 'REGULAR') {
      form.setValue(
        'acquisition_configuration.valid_day_since_account_signup',
        null,
        {
          shouldValidate: true,
          shouldDirty: true,
        }
      );
    }
  }, [acquisitionType, form]);

  return (
    <>
      <FormControl
        isInvalid={!!errors.target_configuration?.business_segmentations}
      >
        <FormLabel fontSize="sm" fontWeight="medium" color="gray.700">
          Tipe Customer
        </FormLabel>
        <MultiSelect
          id="customer-types"
          placeholder="Semua tipe customer"
          chakraStyles={reactSelectStyles}
          options={customerTypeOptions}
          value={customerTypeValue}
          onChange={onCustomerTypesChange}
          isLoading={isLoadingOptions}
          isDisabled={disabled || isLoadingOptions}
        />
        <FormErrorMessage>
          {
            errors.target_configuration?.business_segmentations
              ?.message as string
          }
        </FormErrorMessage>
      </FormControl>

      <FormControl
        isInvalid={!!errors.target_configuration?.distribution_channels}
      >
        <FormLabel fontSize="sm" fontWeight="medium" color="gray.700">
          Cabang
        </FormLabel>
        <MultiSelect
          id="voucher-branches"
          placeholder="Semua cabang"
          chakraStyles={reactSelectStyles}
          options={branchOptions}
          value={branchValue}
          onChange={onBranchesChange}
          isLoading={isLoadingOptions}
          isDisabled={disabled || isLoadingOptions}
        />
        <FormErrorMessage>
          {
            errors.target_configuration?.distribution_channels
              ?.message as string
          }
        </FormErrorMessage>
      </FormControl>

      <FormControl
        isInvalid={!!errors.target_configuration?.business_categories}
      >
        <FormLabel fontSize="sm" fontWeight="medium" color="gray.700">
          Kategori Restoran
        </FormLabel>
        <MultiSelect
          id="restaurant-categories"
          placeholder="Semua kategori restoran"
          chakraStyles={reactSelectStyles}
          options={restaurantCategoryOptions}
          value={restaurantCategoryValue}
          onChange={onRestaurantCategoriesChange}
          isLoading={isLoadingOptions}
          isDisabled={disabled || isLoadingOptions}
        />
        <FormErrorMessage>
          {errors.target_configuration?.business_categories?.message as string}
        </FormErrorMessage>
      </FormControl>

      <Box display="flex" gap={4} flexDirection={{ base: 'column', md: 'row' }}>
        <FormControl
          isInvalid={!!errors.acquisition_configuration?.acquisition_type}
          flex={showAccountAgeField ? 1 : undefined}
        >
          <FormLabel fontSize="sm" fontWeight="medium" color="gray.700">
            Cara Mendapatkan Voucher
          </FormLabel>
          <Select
            placeholder="Pilih cara mendapatkan voucher"
            fontSize="sm"
            {...register('acquisition_configuration.acquisition_type')}
            isDisabled={disabled}
            sx={disabledInputStyles}
          >
            {ACQUISITION_TYPE_SELECT_OPTIONS.map((option) => (
              <option key={option.value} value={option.value}>
                {option.label}
              </option>
            ))}
          </Select>
          <FormErrorMessage>
            {
              errors.acquisition_configuration?.acquisition_type
                ?.message as string
            }
          </FormErrorMessage>
        </FormControl>

        {showAccountAgeField && (
          <FormControl
            isInvalid={
              !!errors.acquisition_configuration?.valid_day_since_account_signup
            }
            flex={1}
          >
            <FormLabel fontSize="sm" fontWeight="medium" color="gray.700">
              Berlaku Sejak Akun Dibuat Selama
            </FormLabel>
            <Select
              placeholder="Pilih durasi"
              fontSize="sm"
              {...register(
                'acquisition_configuration.valid_day_since_account_signup',
                { valueAsNumber: true }
              )}
              isDisabled={disabled}
              sx={disabledInputStyles}
            >
              {ACCOUNT_AGE_SELECT_OPTIONS.map((option) => (
                <option key={option.value} value={option.value}>
                  {option.label}
                </option>
              ))}
            </Select>
            <FormErrorMessage>
              {
                errors.acquisition_configuration?.valid_day_since_account_signup
                  ?.message as string
              }
            </FormErrorMessage>
          </FormControl>
        )}
      </Box>
    </>
  );
};
